import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit'
import { readingsApi, type ListReadingsParams } from '../api/readingsApi'
import type { ContentReading } from '../types'
import type { RootState } from '@/store/store'
import type { AssignedEntityTaxonomy } from '../../taxonomies/types'
import type { ContentAudio } from '../../common/types'
import type { ContentSentence } from '../../sentences/types'

interface ContentReadingState {
  items: Record<string, ContentReading> // normalized cache by ID
  list: ContentReading[]
  pagination: {
    page: number
    size: number
    total: number
  }
  listStatus: 'idle' | 'loading' | 'succeeded' | 'failed'
  listError: string | null
  detailStatus: Record<string, 'idle' | 'loading' | 'succeeded' | 'failed'>
  detailError: Record<string, string | null>
}

const initialState: ContentReadingState = {
  items: {},
  list: [],
  pagination: {
    page: 1,
    size: 20,
    total: 0,
  },
  listStatus: 'idle',
  listError: null,
  detailStatus: {},
  detailError: {},
}

// Fetch list of readings with caching
export const fetchReadingsList = createAsyncThunk<
  { items: ContentReading[]; pagination: { page: number; size: number; total: number } },
  ListReadingsParams | undefined,
  { state: RootState }
>('contentReading/fetchList', async (params) => {
  const res = await readingsApi.listReadings(params)
  return { items: res.items, pagination: res.pagination }
})

// Fetch single reading detail with strict caching (0 network calls if already loaded with sentences/taxonomies)
export const fetchReadingDetailById = createAsyncThunk<
  ContentReading,
  string,
  { state: RootState }
>(
  'contentReading/fetchDetailById',
  async (id: string) => {
    return await readingsApi.getReading(id)
  },
  {
    condition: (id, { getState }) => {
      const state = getState() as RootState
      const readingState = state.contentReadings
      if (!readingState) return true
      if (readingState.detailStatus[id] === 'loading') return false
      const existing = readingState.items[id]
      if (existing?.title_sentence && existing.sentences && existing.questions) {
        return false
      }
      return true
    },
  }
)

export const contentReadingSlice = createSlice({
  name: 'contentReadings',
  initialState,
  reducers: {
    setReadingDetail: (state, action: PayloadAction<ContentReading>) => {
      const item = action.payload
      state.items[item.id] = { ...(state.items[item.id] || {}), ...item }
      // Also update in list if present
      const listIndex = state.list.findIndex((r) => r.id === item.id)
      if (listIndex !== -1) {
        state.list[listIndex] = { ...state.list[listIndex], ...item }
      }
    },
    updateReadingSentence: (state, action: PayloadAction<{ readingId: string; sentence: ContentSentence }>) => {
      const { readingId, sentence } = action.payload
      for (const reading of [state.items[readingId], state.list.find((item) => item.id === readingId)]) {
        if (!reading) continue
        if (reading.title_sentence_id === sentence.id) {
          reading.title_sentence = sentence
          reading.title_text = sentence.text
        }
        const mapping = reading.sentences?.find((item) => item.sentence_id === sentence.id)
        if (mapping) mapping.sentence = sentence
      }
    },
    addSentenceAudio: (state, action: PayloadAction<{ readingId: string; sentenceId: string; audio: ContentAudio }>) => {
      const { readingId, sentenceId, audio } = action.payload
      for (const reading of [state.items[readingId], state.list.find((item) => item.id === readingId)]) {
        const sentence = reading?.title_sentence_id === sentenceId
          ? reading.title_sentence
          : reading?.sentences?.find((item) => item.sentence_id === sentenceId)?.sentence
        if (sentence) {
          sentence.media ??= { audio: [] }
          if (!sentence.media.audio.some((item) => item.id === audio.id)) sentence.media.audio.push(audio)
        }
      }
    },
    updateReadingTaxonomiesInStore: (
      state,
      action: PayloadAction<{ readingId: string; taxonomies: AssignedEntityTaxonomy[] }>
    ) => {
      const { readingId, taxonomies } = action.payload
      if (state.items[readingId]) {
        state.items[readingId].taxonomies = taxonomies
      }
      const listIndex = state.list.findIndex((r) => r.id === readingId)
      if (listIndex !== -1) {
        state.list[listIndex].taxonomies = taxonomies
      }
    },
    updateReadingCoverImageInStore: (
      state,
      action: PayloadAction<{ readingId: string; image: string | null }>
    ) => {
      const { readingId, image } = action.payload
      if (state.items[readingId]) {
        state.items[readingId].image = image
      }
      const listIndex = state.list.findIndex((r) => r.id === readingId)
      if (listIndex !== -1) {
        state.list[listIndex].image = image
      }
    },
    removeReadingFromStore: (state, action: PayloadAction<string>) => {
      const id = action.payload
      delete state.items[id]
      state.list = state.list.filter((r) => r.id !== id)
      state.pagination.total = Math.max(0, state.pagination.total - 1)
    },
    clearReadingList: (state) => {
      state.list = []
      state.pagination.total = 0
      state.listStatus = 'idle'
    },
  },
  extraReducers: (builder) => {
    builder
      // List
      .addCase(fetchReadingsList.pending, (state) => {
        state.listStatus = 'loading'
        state.listError = null
      })
      .addCase(fetchReadingsList.fulfilled, (state, action) => {
        state.listStatus = 'succeeded'
        state.list = action.payload.items
        state.pagination = action.payload.pagination
        // Populate cache with items from list
        action.payload.items.forEach((item) => {
          state.items[item.id] = { ...(state.items[item.id] || {}), ...item }
        })
      })
      .addCase(fetchReadingsList.rejected, (state, action) => {
        state.listStatus = 'failed'
        state.listError = action.error.message || 'Lỗi tải danh sách bài đọc'
      })
      // Detail
      .addCase(fetchReadingDetailById.pending, (state, action) => {
        const id = action.meta.arg
        state.detailStatus[id] = 'loading'
        state.detailError[id] = null
      })
      .addCase(fetchReadingDetailById.fulfilled, (state, action) => {
        const item = action.payload
        state.items[item.id] = item
        state.detailStatus[item.id] = 'succeeded'
        // Also sync basic fields into list if present
        const listIndex = state.list.findIndex((r) => r.id === item.id)
        if (listIndex !== -1) {
          state.list[listIndex] = { ...state.list[listIndex], ...item }
        }
      })
      .addCase(fetchReadingDetailById.rejected, (state, action) => {
        const id = action.meta.arg
        if (action.meta.condition) return
        state.detailStatus[id] = 'failed'
        state.detailError[id] = action.error.message || 'Lỗi tải chi tiết bài đọc'
      })
  },
})

export const {
  setReadingDetail,
  updateReadingSentence,
  addSentenceAudio,
  updateReadingTaxonomiesInStore,
  updateReadingCoverImageInStore,
  removeReadingFromStore,
  clearReadingList,
} = contentReadingSlice.actions

export const selectReadingsList = (state: RootState) => state.contentReadings?.list || []
export const selectReadingsPagination = (state: RootState) =>
  state.contentReadings?.pagination || { page: 1, size: 20, total: 0 }
export const selectReadingsListStatus = (state: RootState) =>
  state.contentReadings?.listStatus || 'idle'
export const selectReadingById = (id: string | null) => (state: RootState) =>
  id ? state.contentReadings?.items[id] || null : null
export const selectReadingDetailStatus = (id: string | null) => (state: RootState) =>
  id ? state.contentReadings?.detailStatus[id] || 'idle' : 'idle'
export const selectReadingDetailError = (id: string | null) => (state: RootState) =>
  id ? state.contentReadings?.detailError[id] || null : null

export default contentReadingSlice.reducer
